export const ONBOARDING_STEPS = ['language', 'phone', 'hear', 'alerts', 'quiet', 'done'] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

const clamp = (index: number): number => Math.max(0, Math.min(index, ONBOARDING_STEPS.length - 1));

export const stepAt = (index: number): OnboardingStep => ONBOARDING_STEPS[clamp(index)];
export const isLastStep = (index: number): boolean => index >= ONBOARDING_STEPS.length - 1;

export type WizardDirection = 'forward' | 'back';

export interface WizardState {
  readonly index: number;
  readonly moved: boolean;
  readonly direction: WizardDirection;
}

export const initialWizard: WizardState = { index: 0, moved: false, direction: 'forward' };

export type WizardAction =
  | { type: 'begin' }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'goTo'; step: OnboardingStep };

export const wizardReducer = (state: WizardState, action: WizardAction): WizardState => {
  switch (action.type) {
    case 'begin':
      return { index: 0, moved: true, direction: 'forward' };
    case 'next':
      return { index: clamp(state.index + 1), moved: true, direction: 'forward' };
    case 'back':
      return { index: clamp(state.index - 1), moved: true, direction: 'back' };
    case 'goTo': {
      const index = ONBOARDING_STEPS.indexOf(action.step);
      return { index, moved: true, direction: index < state.index ? 'back' : 'forward' };
    }
  }
};

export type StepEnterFrom = WizardDirection | 'none';

export const enterFromOf = (state: WizardState): StepEnterFrom => (state.moved ? state.direction : 'none');

export type StepGate = 'loading' | 'failed' | 'ready';

export const stepGateOf = (load: { readonly hasData: boolean; readonly isError: boolean }): StepGate => {
  if (load.hasData) return 'ready';
  return load.isError ? 'failed' : 'loading';
};

export type StepNotice =
  | { readonly kind: 'failed'; readonly text: string }
  | { readonly kind: 'paused'; readonly text?: string }
  | { readonly kind: 'saving' }
  | { readonly kind: 'loading'; readonly text: string }
  | { readonly kind: 'info'; readonly text: string };

export interface StepNoticeInput {
  readonly failed?: string | null;
  readonly paused?: boolean;
  readonly pausedText?: string;
  readonly saving?: boolean;
  readonly loading?: string | null;
  readonly info?: string | null;
}

const present = (text: string | null | undefined): text is string => typeof text === 'string' && text !== '';

export const stepNoticeOf = ({ failed, paused, pausedText, saving, loading, info }: StepNoticeInput): StepNotice | null => {
  if (present(failed)) return { kind: 'failed', text: failed };
  if (paused === true) return present(pausedText) ? { kind: 'paused', text: pausedText } : { kind: 'paused' };
  if (saving === true) return { kind: 'saving' };
  if (present(loading)) return { kind: 'loading', text: loading };
  if (present(info)) return { kind: 'info', text: info };
  return null;
};
